import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import { Grid } from '@mui/material';
import GenericEntityFlowEditTransitionsComponent from './GenericEntityFlowEditTransitionsComponent';
import { get, sequence } from '@totalsoft/change-tracking-react';
import { isPropertyDirty } from '@totalsoft/change-tracking';
import { CollapseCard, Typography } from '@totalsoft/rocket-ui';
import { isValid } from '@totalsoft/pure-validations-react';
import FlexBox from 'components/common/FlexBox';
import IconButton from 'components/common/buttons/IconButton';
import LabeledCheckbox from 'components/common/inputs/LabeledCheckbox';
import { FlowPermissionType, useFlowPermissions } from '../hooks';
import { usePermissions } from 'providers/RightsHooks';

const GenericEntityFlowEditTransitionsListComponent = ({
  flowId,
  transitionsLens,
  transitionsDirtyInfo,
  userGroups,
  businessStates,
  onSaveTransition,
  handleDeleteTransition,
  transitionsValidation,
  saving
}) => {
  const { t } = useTranslation();

  const authorizationCode = useFlowPermissions(FlowPermissionType.WRITE);
  const canWrite = usePermissions(authorizationCode);

  return (
    <FlexBox direction='column' gap={1}>
      {transitionsLens &&
        (transitionsLens.genericEntityFlowTransitions |> sequence).length > 0 &&
        (transitionsLens.genericEntityFlowTransitions |> sequence).map((item, idx) => {
          const propertyPath = 'genericEntityFlowTransitions[' + idx.toString() + ']'; //
          const isDirty = isPropertyDirty(propertyPath, transitionsDirtyInfo);

          const initialStateName = businessStates.find(x => x.stateId === (item.initialStateId |> get))?.stateName;
          const finalStateName = businessStates.find(x => x.stateId === (item.finalStateId |> get))?.stateName;

          return (
            <CollapseCard
              key={`${item.initialStateId |> get}_${item.finalStateId |> get}_${idx}`}
              hideSubheaderOnExpand
              defaultExpanded={!(item.transitionId |> get)}
              subheader={
                <Grid container spacing={2} alignItems='center'>
                  <Grid item xs={12} md={2}>
                    <Tooltip title={initialStateName} placement='bottom-start'>
                      {/* This span wrapper is needed for tooltip attachment. This is because rocket's Typography doesn't do ref forwarding. Needs to be fixed */}
                      <span>
                        <Typography color='textPrimary' textOverflow='ellipsis' overflow='hidden' noWrap>
                          {initialStateName || t('GenericEntityFlow.NoInitialState')}
                        </Typography>
                      </span>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Tooltip title={finalStateName} placement='bottom-start'>
                      <span>
                        <Typography color='textPrimary' textOverflow='ellipsis' overflow='hidden' noWrap>
                          {finalStateName || t('GenericEntityFlow.NoFinalState')}
                        </Typography>
                      </span>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <LabeledCheckbox
                      checked={Boolean(item.allowedForAllUserGroups |> get)}
                      disabled={true}
                      label={t('GenericEntityFlow.AllGroups')}
                      checkboxProps={{ sx: { py: 0 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <LabeledCheckbox
                      checked={Boolean(item.fourEyesPrinciple |> get)}
                      disabled={true}
                      label={t('GenericEntityFlow.FourEyesPrinciple')}
                      checkboxProps={{ sx: { py: 0 } }}
                    />
                  </Grid>
                </Grid>
              }
              actions={
                <FlexBox gap={0.5}>
                  <IconButton
                    type='save'
                    onClick={() => onSaveTransition(item)}
                    disabled={!canWrite || saving || !isDirty || !isValid(transitionsValidation)}
                  />
                  <IconButton type='delete' onClick={() => handleDeleteTransition(idx)} disabled={!canWrite || saving} />
                </FlexBox>
              }
            >
              <GenericEntityFlowEditTransitionsComponent
                flowId={flowId}
                businessStates={businessStates}
                transition={item}
                userGroups={userGroups}
                transitionsValidation={transitionsValidation}
                index={idx}
              />
            </CollapseCard>
          );
        })}
    </FlexBox>
  );
};

GenericEntityFlowEditTransitionsListComponent.propTypes = {
  flowId: PropTypes.number,
  transitionsLens: PropTypes.object,
  transitionsDirtyInfo: PropTypes.object,
  userGroups: PropTypes.array,
  businessStates: PropTypes.array,
  onSaveTransition: PropTypes.func,
  handleDeleteTransition: PropTypes.func,
  transitionsValidation: PropTypes.object,
  saving: PropTypes.bool
};

export default React.memo(GenericEntityFlowEditTransitionsListComponent);
